/**
 * Carrying a Google sign-in across the provider round trip.
 *
 * The authorize request used to name the current page as its `redirect_uri`,
 * which meant Google had to have every page offering a Sign in with Google
 * button on its allow-list — including one entry per product page, which cannot
 * be enumerated. Anyone starting from a tour page got `redirect_uri_mismatch`.
 *
 * Now a single fixed callback route is named instead, and the page to come back
 * to is remembered here. One URI per environment is registered and it works from
 * anywhere.
 */
const HANDOFF_KEY = "googleOAuthHandoff";

export interface IGoogleOAuthHandoff {
    /** Echoed through OAuth's `state` and checked on the way back. */
    nonce: string;
    /** Where the visitor was when they pressed the button. */
    returnPath: string;
}

/**
 * The single page Google is asked to come back to.
 *
 * Deliberately `/login` rather than a dedicated callback route: that URI is
 * already registered on the OAuth client — signing in from the login page has
 * always worked — so this needs no change in the Google console for any
 * environment. A new path would have had to be added for every origin first, and
 * a deploy that got ahead of that would break Google sign-in everywhere rather
 * than only on product pages.
 */
export const GOOGLE_OAUTH_CALLBACK_PATH = "/login";

const randomNonce = (): string => {
    // A real anti-forgery value, so a crafted callback cannot pass itself off as
    // a sign-in this app started. Math.random is not a cryptographic source.
    try {
        const bytes = new Uint32Array(4);
        crypto.getRandomValues(bytes);
        return Array.from(bytes, (b) => b.toString(16).padStart(8, "0")).join("");
    } catch {
        return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    }
};

export const startGoogleOAuthHandoff = (returnPath: string): string => {
    const nonce = randomNonce();
    try {
        sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ nonce, returnPath }));
    } catch {
        // Without storage the round trip cannot be verified; the callback will
        // send the visitor home rather than trust an unverifiable token.
    }
    return nonce;
};

export const readGoogleOAuthHandoff = (): IGoogleOAuthHandoff | null => {
    try {
        const raw = sessionStorage.getItem(HANDOFF_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return typeof parsed?.nonce === "string" && typeof parsed?.returnPath === "string"
            ? (parsed as IGoogleOAuthHandoff)
            : null;
    } catch {
        return null;
    }
};

export const clearGoogleOAuthHandoff = () => {
    try {
        sessionStorage.removeItem(HANDOFF_KEY);
    } catch {
        // A stale entry is only ever compared against a fresh nonce.
    }
};

/**
 * Where to send the visitor once the callback has checked the state.
 *
 * Returns null when the round trip cannot be trusted — no token, no record of
 * having started one, or a `state` that does not match the nonce we issued.
 */
export const resolveGoogleOAuthReturn = (
    hash: string,
    stored: IGoogleOAuthHandoff | null,
): string | null => {
    const params = new URLSearchParams(hash.replace(/^#/, ""));
    const accessToken = params.get("access_token");
    const state = params.get("state");

    if (!accessToken || !stored || !state || state !== stored.nonce) return null;

    // The token travels on to the original page in the fragment, exactly as it
    // used to arrive there, so every existing handler keeps working unchanged.
    // A fragment is never sent to a server.
    params.delete("state");
    return `${stored.returnPath}#${params.toString()}`;
};
